import { ViewCard, ViewList } from '@config/icons'
import type { UseRateHookReturn } from '@entities/avail/dtos/types'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils/index'

import { Button } from '../ui/button'

interface ToggleListGridProps {
  onClick: (value: boolean) => void
  cardShowing: boolean
  rate?: UseRateHookReturn
  className?: string
}

export default function ToggleListGrid({
  onClick,
  cardShowing,
  rate,
  className,
}: ToggleListGridProps): JSX.Element {
  const { t } = useTranslation()
  const [, setHasClickDataViewToggle] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    const getCardShowing = localStorage.getItem('cardShowing')
    const getHasClickDataViewToggle = localStorage.getItem(
      'hasClickDataViewToggle',
    )
    if (getCardShowing) {
      const parsedCardShowing = JSON.parse(getCardShowing)
      onClick(parsedCardShowing)
    }
    if (getHasClickDataViewToggle) {
      setHasClickDataViewToggle(true)
    } else {
      setHasClickDataViewToggle(false)
    }
  }, [])

  const toggleDataView = () => {
    // Set transitioning state immediately for visual feedback
    setIsTransitioning(true)

    // Clear rate selection if needed - do this immediately
    if (rate) rate.setSelectedRates([])

    // Update UI state immediately for responsiveness
    onClick(!cardShowing)

    // Handle localStorage in the background
    setTimeout(() => {
      localStorage.setItem('cardShowing', JSON.stringify(!cardShowing))
      localStorage.setItem('hasClickDataViewToggle', JSON.stringify(true))
      setHasClickDataViewToggle(true)
      setIsTransitioning(false)
    }, 0)
  }

  interface optionItemProps {
    text: string
    icon: string
  }

  type optionsProps = Record<string, optionItemProps>

  const listMode = t('listMode')
  const cardMode = t('cardMode')

  const options: optionsProps = {
    true: {
      text: listMode,
      icon: ViewList,
    },
    false: {
      text: cardMode,
      icon: ViewCard,
    },
  }

  const key = cardShowing.toString()

  // Animation variants for the button
  const buttonVariants = {
    initial: { opacity: 1 },
    pressed: { scale: 0.95, opacity: 0.8 },
    hover: { scale: 1.03 },
  }

  // Animation variants for the icon
  const iconVariants = {
    initial: { rotate: 0 },
    rotating: { rotate: 180, transition: { duration: 0.3 } },
  }

  return (
    <motion.div
      initial="initial"
      whileHover="hover"
      whileTap="pressed"
      variants={buttonVariants}
      className={cn('hidden md:block', className)}
    >
      <Button
        type="button"
        variant="outline"
        data-state={options[key].text === listMode ? 'card' : 'list'}
        className={cn(
          'hidden md:flex font-medium text-sm p-2 w-full items-center gap-2 rounded-md border joyride-tourResults-CardListButton',
          isTransitioning && 'opacity-80',
          className,
        )}
        onClick={toggleDataView}
      >
        <motion.div
          variants={iconVariants}
          animate={isTransitioning ? 'rotating' : 'initial'}
          className="flex items-center justify-center"
        >
          <img
            className="size-5"
            src={options[key].icon}
            alt={options[key].text}
          />
        </motion.div>
        <motion.p
          className="whitespace-nowrap"
          initial={{ opacity: 1 }}
          animate={{
            opacity: isTransitioning ? 0.5 : 1,
            transition: { duration: 0.2 },
          }}
        >
          {options[key].text}
        </motion.p>
      </Button>
    </motion.div>
  )
}
